/**
 * Shared chart + KPI body for one RSI(2) Oversold Pullback idea -- the
 * candlestick chart, RSI(2) sub-panel, range toggle (1W/3M/6M/1Y), outcome
 * summary line, and collapsible disclaimer. Used both inside
 * `Rsi2PullbackPage.tsx`'s `ChartDialog` modal (where the table `row` is
 * already known, so the KPI strip can render instantly without waiting on
 * `getDetail`) and the standalone `/investing-idea/rsi2-pullback/chart`
 * full-page route opened via "Open in new tab" (where only `symbol`/
 * `signalDate` are known from the URL).
 *
 * `Rsi2PullbackIdeaDetailResponse` extends `Rsi2PullbackIdeaRow` with the
 * exact fields `ChartKpiStrip`/`ChartOutcomeSummary` need, so when no
 * `initialRow` is supplied this falls back to the fetched `detail` once it
 * arrives -- no separate "standalone" KPI shape is needed.
 */
import { useEffect, useState } from "react";
import { Alert, Box, CircularProgress, Collapse, IconButton, Stack, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import rsi2PullbackService from "@/services/rsi2PullbackService";
import type { Rsi2PullbackChartResponse, Rsi2PullbackIdeaDetailResponse, Rsi2PullbackIdeaRow } from "@/types";
import { extractErrorMessage } from "@/utils/errors";
import { ChartKpiStrip, ChartOutcomeSummary, windowChart, type ChartRange } from "./rsi2ChartHelpers";
import Rsi2CandleChart from "./Rsi2CandleChart";
import Rsi2RsiPanel from "./Rsi2RsiPanel";

const RANGE_OPTIONS: { value: ChartRange; label: string; aria: string }[] = [
  { value: "1W", label: "1W", aria: "1 week" },
  { value: "3M", label: "3M", aria: "3 months" },
  { value: "6M", label: "6M", aria: "6 months" },
  { value: "1Y", label: "1Y", aria: "1 year" },
];

export interface Rsi2ChartPanelProps {
  symbol: string;
  signalDate: string;
  /** Already-known table row, if any -- lets the KPI strip render instantly
   * instead of waiting on `getDetail`. Omitted on the standalone page,
   * which only has `symbol`/`signalDate` from the URL. */
  initialRow?: Rsi2PullbackIdeaRow;
  defaultRange?: ChartRange;
  /** Renders an "Open in new tab" icon button next to the range toggle when
   * provided -- shown from `ChartDialog` (links to the standalone page),
   * omitted on the standalone page itself. */
  onOpenInNewTab?: () => void;
}

export function Rsi2ChartPanel({
  symbol,
  signalDate,
  initialRow,
  defaultRange = "6M",
  onOpenInNewTab,
}: Rsi2ChartPanelProps) {
  const [chart, setChart] = useState<Rsi2PullbackChartResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [detail, setDetail] = useState<Rsi2PullbackIdeaDetailResponse | null>(null);
  const [detailLoading, setDetailLoading] = useState(true);
  const [range, setRange] = useState<ChartRange>(defaultRange);
  const [disclaimerOpen, setDisclaimerOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    rsi2PullbackService
      .getChart(symbol, signalDate)
      .then((data) => {
        if (!cancelled) setChart(data);
      })
      .catch((err) => {
        if (!cancelled) setError(extractErrorMessage(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [symbol, signalDate]);

  useEffect(() => {
    let cancelled = false;
    setDetailLoading(true);
    rsi2PullbackService
      .getDetail(symbol, signalDate)
      .then((data) => {
        if (!cancelled) setDetail(data);
      })
      .catch(() => {
        // The outcome summary / KPI-strip fallback is supplementary -- the
        // chart itself already rendered from `getChart`, so fail silently
        // rather than blocking the whole panel with an error banner.
      })
      .finally(() => {
        if (!cancelled) setDetailLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [symbol, signalDate]);

  const kpiRow: Rsi2PullbackIdeaRow | null = initialRow ?? detail;
  const windowed = chart ? windowChart(chart, range) : null;
  const labels = windowed?.candles.map((c) => c.trade_date) ?? [];

  return (
    <Stack spacing={1.5}>
      {kpiRow && <ChartKpiStrip row={kpiRow} />}
      {!kpiRow && detailLoading && (
        <Box sx={{ display: "flex", py: 1 }}>
          <CircularProgress size={20} />
        </Box>
      )}

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      )}
      {error && <Alert severity="error">{error}</Alert>}
      {chart && windowed && (
        <>
          <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={1}>
            <ToggleButtonGroup
              value={range}
              exclusive
              size="small"
              onChange={(_e, value: ChartRange | null) => {
                if (value) setRange(value);
              }}
              aria-label="Chart range"
            >
              {RANGE_OPTIONS.map((opt) => (
                <ToggleButton key={opt.value} value={opt.value} aria-label={opt.aria}>
                  {opt.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
            {onOpenInNewTab && (
              <Tooltip title="Open in new tab">
                <IconButton size="small" aria-label="Open in new tab" onClick={onOpenInNewTab}>
                  <OpenInNewIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>

          <Rsi2CandleChart
            candles={windowed.candles}
            sma50={windowed.sma50}
            sma200={windowed.sma200}
            signalDate={chart.signal_date}
            referencePrice={chart.reference_price}
            indicativeTargetPrice={chart.indicative_target_price}
            indicativeRiskLevel={chart.indicative_risk_level}
            targetHitDate={chart.target_hit_date}
          />
          <Rsi2RsiPanel labels={labels} rsi2={windowed.rsi2} signalDate={chart.signal_date} />

          {kpiRow && (
            <ChartOutcomeSummary row={kpiRow} chart={chart} detail={detail} detailLoading={detailLoading} />
          )}

          <Box>
            <Stack
              direction="row"
              spacing={0.5}
              alignItems="center"
              sx={{ cursor: "pointer", width: "fit-content" }}
              onClick={() => setDisclaimerOpen((open) => !open)}
            >
              <InfoOutlinedIcon fontSize="small" color="action" />
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                Educational disclaimer
              </Typography>
              <ExpandMoreIcon
                fontSize="small"
                color="action"
                sx={{ transform: disclaimerOpen ? "rotate(180deg)" : "none", transition: "transform 0.15s" }}
              />
            </Stack>
            <Collapse in={disclaimerOpen}>
              <Alert severity="info" variant="outlined" sx={{ mt: 1 }}>
                {chart.disclaimer}
              </Alert>
            </Collapse>
          </Box>
        </>
      )}
    </Stack>
  );
}

export default Rsi2ChartPanel;
